import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";

const ROTATE_INTERVAL_MS = 6000;
const DISMISS_KEY = "promo_banner_dismissed_v1";

/**
 * Sticky-top rotating promotional banners.
 * Hidden on admin/owner/login/review/pay routes.
 * Remembers per-banner dismissal in localStorage (24h TTL).
 */
export default function PromoBanner() {
  const { lang } = useI18n();
  const location = useLocation();
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return {};
      const data = JSON.parse(raw);
      const now = Date.now();
      const fresh = {};
      Object.entries(data || {}).forEach(([id, ts]) => {
        if (now - ts < 24 * 60 * 60 * 1000) fresh[id] = ts;
      });
      return fresh;
    } catch {
      return {};
    }
  });

  // Hide on admin, owner, login, review, pay
  const hidden = useMemo(() => {
    const p = location.pathname || "";
    return (
      p.startsWith("/admin") ||
      p.startsWith("/owner") ||
      p.startsWith("/login") ||
      p.startsWith("/review") ||
      p.startsWith("/pay")
    );
  }, [location.pathname]);

  useEffect(() => {
    if (hidden) return;
    api
      .get("/promo-banners")
      .then((r) => setBanners(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBanners([]));
  }, [hidden]);

  const visibleBanners = useMemo(
    () => banners.filter((b) => !dismissedIds[b.id]),
    [banners, dismissedIds]
  );

  // Reset idx when count changes
  useEffect(() => {
    if (idx >= visibleBanners.length) setIdx(0);
  }, [visibleBanners.length, idx]);

  // Auto-rotate
  useEffect(() => {
    if (visibleBanners.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % visibleBanners.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [visibleBanners.length]);

  const dismiss = useCallback(
    (id) => {
      const next = { ...dismissedIds, [id]: Date.now() };
      setDismissedIds(next);
      try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [dismissedIds]
  );

  if (hidden) return null;
  if (!visibleBanners.length) return null;

  const b = visibleBanners[Math.min(idx, visibleBanners.length - 1)];
  if (!b) return null;
  const message = (lang === "en" ? b.message_en : b.message_ro) || b.message_ro || b.message_en;
  const ctaLabel = (lang === "en" ? b.cta_label_en : b.cta_label_ro) || "";
  const hasCta = !!(ctaLabel && b.cta_href);

  return (
    <div
      role="region"
      aria-label="promo-banner"
      data-testid="promo-banner"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        width: "100%",
        background: b.bg_color || "#D4AF37",
        color: b.text_color || "#0A0A0A",
      }}
      className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 text-sm shadow-md"
    >
      <div className="flex-1 text-center font-medium tracking-wide truncate sm:whitespace-normal">
        <span data-testid="promo-banner-message">{message}</span>
        {hasCta && (
          <a
            href={b.cta_href}
            className="ml-3 inline-block underline underline-offset-2 font-semibold hover:opacity-80"
            data-testid="promo-banner-cta"
          >
            {ctaLabel} →
          </a>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {visibleBanners.length > 1 && (
          <div className="flex items-center gap-1" aria-label="rotation-dots">
            {visibleBanners.map((_, i) => (
              <span
                key={i}
                className="inline-block w-1.5 h-1.5 rounded-full transition-opacity"
                style={{ background: b.text_color || "#0A0A0A", opacity: i === idx ? 0.95 : 0.35 }}
              />
            ))}
          </div>
        )}
        <button
          onClick={() => dismiss(b.id)}
          className="opacity-70 hover:opacity-100 ml-1"
          aria-label="dismiss banner"
          data-testid="promo-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
